import {ExtendedModInfo, ModsDetector} from './modsDetector';
import ModsLoadersRepository from './modsLoadersRepository';
import type {ModsLoader} from './modsLoader';
import topologicalSort from './topologicalSort';
import {showFatalError, showMissingModError} from './notifications';
import {getDetailedName, getExistsSync, getReaddirAsync, isBrowser, queryFile, resolvePath} from './utils';
import {extname, join} from 'path';
import VirtualPathsStorage, {convertPathToUrl, IVirtualPathResolver} from './virtualFileSystem';
import logger from './logging';
import satisfies from 'semver/functions/satisfies';

const virtualPathsStorage = new VirtualPathsStorage();

let isErrorDisplayed = false;

async function fatalErrorHandler(error: any) {
    if (!isErrorDisplayed) {
        isErrorDisplayed = true;
        await showFatalError(error);
        isErrorDisplayed = false;
    }
    return false;
}

window.addEventListener('error', (e) => fatalErrorHandler(e.error))
window.addEventListener('unhandledrejection', (e) => fatalErrorHandler(e.reason))

async function loadScript(url: string) {
    return new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.async = false;
        script.onload = () => resolve();
        script.onerror = reject;
        script.onabort = reject;
        script.onabort = reject;

        document.body.appendChild(script);
    });
}

async function initializeLoaders(): Promise<ModsLoadersRepository> {
    logger.debug('Initializing mod loaders.');
    const modsLoaders = new ModsLoadersRepository();

    if (isBrowser()) {
        logger.info('Mod loaders are not supported in browser.');
        return modsLoaders;
    }

    const loadersFolder = resolvePath('js', 'loaders');
    const loadersScripts: string[] = [];

    if (isBrowser()) {
        // TODO: Implement mods list for browser.
        // loadersScripts.push(resolvePath('browser.mods.json'));
    } else {
        const existsSync = await getExistsSync();
        const isFolderExist = existsSync(loadersFolder);
        if (!isFolderExist) {
            logger.info({loadersFolder}, 'No mod loaders found');
            return modsLoaders;
        }

        const readdirAsync = await getReaddirAsync();

        for (const entry of await readdirAsync(loadersFolder)) {
            if (extname(entry).toLowerCase() === '.js') {
                loadersScripts.push(entry);
            }
        }
    }

    logger.info({loadersFolder, loadersScripts}, 'Mod loaders found');
    const loadPromises = loadersScripts.map(async (script) => {
        try {
            const loaderPath = './js/loaders/' + script;
            const loaderModule = await requireLoader(loaderPath);

            modsLoaders.registerLoader(loaderModule?.default);
            logger.info({loader: script}, 'Loader is initialized successfully.');
        } catch (err) {
            logger.error({loader: script, err}, 'Failed to initialize loader');
        }
    });

    await Promise.all(loadPromises);
    return modsLoaders;
}

async function requireLoader(loaderPath: string) {
    const loaderScript = await queryFile(loaderPath);
    return executeLoaderModule(loaderScript);
}

function executeLoaderModule(script: string): any {
    const module = {
        exports: {}
    }

    eval(script);

    return module.exports;
}

async function getLoaders(): Promise<ModsLoader[]> {
    const modsLoadersRepository = await initializeLoaders();
    return modsLoadersRepository.getLoaders();
}

async function detectMods(loaders: ModsLoader[]): Promise<Map<string, ExtendedModInfo>> {
    const aggregatedMods = new Map<string, ExtendedModInfo>();

    loaders.sort((firstLoader, secondLoader) => firstLoader.getOrder() - secondLoader.getOrder());

    for (const loader of loaders) {
        const modsLocations = await loader.getPaths(virtualPathsStorage);
        logger.info({loader: loader.name, modsLocations}, 'Loader resolved mods locations');

        for (const location of modsLocations) {
            const modsDetector: ModsDetector = loader.getModsDetector();
            const {mods, dependencies} = await modsDetector.detect(location);
            logger.info({location, mods: mods.map((mod) => mod.name)}, 'Mods found');

            for (const mod of mods) {
                const displayedModName = mod.parameters.displayedName
                    ? `'${mod.parameters.displayedName}' (${mod.name})`
                    : `'${mod.name}'`;

                if (aggregatedMods.has(mod.name)) {
                    throw new Error(
                        `Detected mods with duplicate names: '${mod.name}'.\n` +
                        `Either remove ${displayedModName} from '${loader.name}' or from '${aggregatedMods.get(mod.name)?.source}'.`
                    );
                }

                const modVersion = mod.parameters.version ?? '';
                for (const versionConstraint of mod.parameters.requiredVersion) {
                    if (!mod.parameters.optional && !satisfies(modVersion, versionConstraint)) {
                        throw new Error(
                            `Install or update mod ${displayedModName}.\n` +
                            `Version requirement '${versionConstraint}' is not satisfied.`
                        )
                    }
                }

                aggregatedMods.set(mod.name, {...mod, source: loader.name, location});
            }

            for (const dependency of dependencies) {
                const mod = aggregatedMods.get(dependency[0]);
                if (!mod) {
                    throw new Error(
                        `Unable to resolve mod from dependency: ${JSON.stringify(dependency)}`
                    );
                }

                mod.requiredBy ??= [];
                mod.requiredBy.push(dependency[1]);
            }
        }
    }

    return aggregatedMods;
}

async function getModUrl(mod: ExtendedModInfo): Promise<string> {
    return convertPathToUrl(join(mod.location, mod.name + '.js'));
}

export async function getModsList(): Promise<ExtendedModInfo[]> {
    try {
        const loaders = await getLoaders();
        const mods = await detectMods(loaders);

        const dependencies: [string, string][] = [];
        for (const mod of mods.values()) {
            if (mod.requiredBy) {
                for (const dependency of mod.requiredBy) {
                    dependencies.push([mod.name, dependency]);
                }
            }
        }

        const orderedModNames = topologicalSort([...mods.keys()], dependencies);

        return orderedModNames
            .map((name) => {
                const modInfo = mods.get(name);
                if (!modInfo) {
                    throw new Error(`Missing mod '${name}'.`);
                }
                return modInfo;
            });
    } catch (err) {
        return await showFatalError(err);
    }
}

export async function loadMods(mods: ExtendedModInfo[]): Promise<void> {
    try {
        for (const mod of mods) {
            const modName = getDetailedName(mod);
            try {
                logger.info({modName}, 'Loading mod');
                await loadScript(await getModUrl(mod));
            } catch (err) {
                if (mod.parameters.optional) {
                    logger.warn({modName}, 'Failed to load optional mod.');
                } else {
                    return await showMissingModError(mod, mods);
                }
            }
        }
    } catch (err) {
        return await showFatalError(err);
    }
}

export const pathResolver: IVirtualPathResolver = virtualPathsStorage;
